/* CO JE NOVÉHO — logika stránky /novinky a tečky v liště (24. 10. 2026).
 *
 * Bez API: poradce si „viděl jsem“ pamatuje v localStorage (datum nejnovějšího
 * vydání, které otevřel). Úložiště může chybět (privátní okno, zakázaná data),
 * proto každé čtení i zápis je v try/catch a záloha drží stav aspoň v paměti,
 * jinak by tečka po otevření stránky svítila dál.
 *
 * Tečka svítí, když nejnovější vydání je novější než poslední viděné.
 * Kdo stránku nikdy neotevřel, tečku vidí (po nasazení tak novinku uvidí všichni).
 */
import { useSyncExternalStore } from "react";
import { NOVINKY } from "@/data/novinky";

export const KLIC_VIDENO = "bp_novinky_videno";

const JE_DATUM = /^\d{4}-\d{2}-\d{2}$/;
const platneDatum = (d) => (typeof d === "string" && JE_DATUM.test(d) ? d : null);

/** Texty stránky a odkazu. Jen čeština a slovenština (viz jazykNovinek). */
export const TEXTY_NOVINEK = {
  cs: {
    nadpis: "Co je nového",
    info: "Přehled změn v portálu, nejnovější nahoře.",
    nove: "Nové",
    video: "Video",
    prazdno: "Zatím tu nejsou žádné novinky.",
    odkazNeprectene: "Co je nového, máte nepřečtené novinky",
  },
  sk: {
    nadpis: "Čo je nové",
    info: "Prehľad zmien v portáli, najnovšie hore.",
    nove: "Nové",
    video: "Video",
    prazdno: "Zatiaľ tu nie sú žiadne novinky.",
    odkazNeprectene: "Čo je nové, máte neprečítané novinky",
  },
};

/** Slovensky pro slovenský účet (země tenanta) nebo slovenské rozhraní, jinak česky. */
export function jazykNovinek(lang, zeme) {
  if (String(zeme || "").toUpperCase() === "SK") return "sk";
  if (String(lang || "").toLowerCase() === "sk") return "sk";
  return "cs";
}

/** Platná vydání, nejnovější první (při stejném datu rozhodne verze). */
export function seradNovinky(vydani = NOVINKY) {
  return (Array.isArray(vydani) ? vydani : [])
    .filter((v) => v && platneDatum(v.datum) && Array.isArray(v.polozky))
    .slice()
    .sort((a, b) => b.datum.localeCompare(a.datum) || String(b.verze || "").localeCompare(String(a.verze || "")));
}

/** Datum nejnovějšího vydání, nebo null, když žádné není. */
export function datumNejnovejsiho(vydani = NOVINKY) {
  return seradNovinky(vydani)[0]?.datum || null;
}

/** Je nejnovější vydání novější než to, které poradce naposled viděl? */
export function maNeprectene(vydani, videno) {
  const nejnovejsi = datumNejnovejsiho(vydani);
  if (!nejnovejsi) return false;
  const posledni = platneDatum(videno);
  return !posledni || nejnovejsi > posledni;
}

/** Bezpečný odkaz na video: jen https, jinak nic (data píše člověk ručně). */
export function odkazVidea(url) {
  const u = String(url || "").trim();
  return /^https:\/\/\S+$/i.test(u) ? u : null;
}

/* ===== „Viděl jsem“ — localStorage + záloha v paměti ===== */

let videnoVPameti = null;
const posluchaci = new Set();

const pozdejsi = (a, b) => {
  const x = platneDatum(a);
  const y = platneDatum(b);
  if (!x) return y;
  if (!y) return x;
  return x > y ? x : y;
};

/** Poslední viděné datum (string nebo null). Stabilní snapshot pro useSyncExternalStore. */
export function nactiVideno() {
  let ulozene = null;
  try {
    ulozene = window.localStorage.getItem(KLIC_VIDENO);
  } catch { /* úložiště nedostupné — platí paměť */ }
  return pozdejsi(ulozene, videnoVPameti);
}

/** Označí vydání jako viděné. Nikdy necouvá na starší datum. */
export function oznacVideno(datum = datumNejnovejsiho()) {
  const nove = platneDatum(datum);
  if (!nove) return;
  const dosud = nactiVideno();
  if (dosud && dosud >= nove) return;
  videnoVPameti = nove;
  try {
    window.localStorage.setItem(KLIC_VIDENO, nove);
  } catch { /* úložiště nedostupné — v paměti to platí i tak */ }
  posluchaci.forEach((cb) => cb());
}

/** Odběr změn: zápis v této záložce i v jiné záložce (událost storage). */
export function odebirejVideno(cb) {
  posluchaci.add(cb);
  const naStorage = (e) => { if (!e || e.key === KLIC_VIDENO || e.key === null) cb(); };
  window.addEventListener("storage", naStorage);
  return () => {
    posluchaci.delete(cb);
    window.removeEventListener("storage", naStorage);
  };
}

/** Hook pro tečku: true = je co číst. */
export function useNovinkyNeprectene(vydani = NOVINKY) {
  const videno = useSyncExternalStore(odebirejVideno, nactiVideno, nactiVideno);
  return maNeprectene(vydani, videno);
}

/** Jen pro testy: zapomenout paměťovou zálohu. */
export function _zapomenPametProTesty() {
  videnoVPameti = null;
}
